/* eslint-disable react-refresh/only-export-components -- ชิ้นส่วนของจอ ຟອມກອກ + helper ของมันอยู่ไฟล์เดียวโดยตั้งใจ */
import { Sparkles } from "../../components/icons";
import type { FormFieldHint, ProposedFormPage } from "../../lib/documentTypes";

/**
 * ขั้นตอนการกรอก (`x-pages` ของ form_schema) — ผู้ดูแลแบ่งฟอร์มยาวเป็นหน้า ๆ ให้ประชาชนกรอกทีละขั้นในแอป
 * แอปอ่านที่ `formPagesOf`/`pageGroups` (web-mobile lib/form-schema.ts) — รูปแบบต้องตรงกัน
 */
export interface FormPage {
  id: string;
  title: string;
  lead: string;
  /** เอกสารที่ให้ผู้ร้องถ่ายรูปให้ AI เติมช่องของขั้นนี้ ("" = ไม่มีปุ่มสแกน) */
  scan: "" | "id_card" | "family_book" | "passport" | "document";
}

export const SCAN_OPTIONS: { value: FormPage["scan"]; label: string }[] = [
  { value: "", label: "ບໍ່ມີ" },
  { value: "id_card", label: "ບັດປະຈຳຕົວ" },
  { value: "family_book", label: "ປື້ມສຳມະໂນຄົວ" },
  { value: "passport", label: "ໜັງສືຜ່ານແດນ" },
  { value: "document", label: "ເອກະສານອື່ນ" },
];

const scanLabel = (s: FormPage["scan"]) => SCAN_OPTIONS.find((o) => o.value === s)?.label ?? s;

export function newPageId(): string {
  return `pg_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * เรียงช่องตามขั้น: ช่องของขั้นผู้ขอ (x-step applicant — ขั้นคงที่ของแอป) ก่อน แล้วตามลำดับขั้นตอน
 * ช่องที่ไม่มีขั้น/ขั้นที่ถูกลบ ไปอยู่ขั้นของช่องก่อนหน้า (กฎเดียวกับแอป) · ไม่มีขั้นตอนเลย = ล้าง page ทิ้ง
 */
export function regroup<F extends { page: string; rawStep: string }>(fields: F[], pages: FormPage[]): F[] {
  const applicant = fields.filter((f) => f.rawStep === "applicant");
  const rest = fields.filter((f) => f.rawStep !== "applicant");
  const first = pages[0];
  if (!first) return [...applicant, ...rest.map((f) => (f.page ? { ...f, page: "" } : f))];
  let prev = first.id;
  const fixed = rest.map((f) => {
    if (pages.some((p) => p.id === f.page)) {
      prev = f.page;
      return f;
    }
    return { ...f, page: prev };
  });
  return [...applicant, ...pages.flatMap((p) => fixed.filter((f) => f.page === p.id))];
}

/** แถบ "ใบนี้มีกี่ขั้นตอนในแอป" — ขั้นคงที่ของแอปเป็นสีเทา ขั้นตอนที่ผู้ดูแลกำหนดกดเพื่อแก้ได้ */
export function StepsSummary({
  pages,
  selected,
  onSelect,
}: {
  pages: FormPage[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const fixedBefore = ["ຢືນຢັນຕົວຕົນ", "ຂໍ້ມູນຜູ້ຂໍ"];
  const fixedAfter = ["ກວດທານ & ເຊັນ", "ຈ່າຍເງິນ"];
  const total = fixedBefore.length + Math.max(pages.length, 1) + fixedAfter.length;
  let n = 0;
  const fixed = (label: string) => {
    n++;
    return (
      <li key={label} className="fp-steps__item is-fixed" title="ຂັ້ນຕອນຄົງທີ່ຂອງແອັບ">
        <span className="fp-steps__no num">{n}</span>
        {label}
      </li>
    );
  };
  return (
    <div className="fp-steps">
      <p className="fp-steps__head">
        ໃນແອັບ ໃບນີ້ມີ <b className="num">{total}</b> ຂັ້ນຕອນ
      </p>
      <ol className="fp-steps__list">
        {fixedBefore.map(fixed)}
        {pages.length === 0 ? (
          fixed("ລາຍລະອຽດຄຳຮ້ອງ (ໜ້າດຽວ)")
        ) : (
          pages.map((p) => {
            n++;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  className={p.id === selected ? "fp-steps__item is-selected" : "fp-steps__item"}
                  onClick={() => onSelect(p.id)}
                >
                  <span className="fp-steps__no num">{n}</span>
                  {p.title || "(ບໍ່ມີຊື່)"}
                  {p.scan ? <Sparkles size={12} /> : null}
                </button>
              </li>
            );
          })
        )}
        {fixedAfter.map(fixed)}
      </ol>
    </div>
  );
}

/** แก้ขั้นตอนหนึ่ง: ชื่อ คำนำ ปุ่มสแกน ลำดับ และลบ */
export function PageEditor({
  page,
  index,
  total,
  fieldCount,
  onChange,
  onMove,
  onRemove,
}: {
  page: FormPage;
  index: number;
  total: number;
  fieldCount: number;
  onChange: (patch: Partial<FormPage>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  return (
    <div className="stack">
      <p className="caption">
        ຂັ້ນຕອນການກອກທີ <span className="num">{index + 1}</span> / <span className="num">{total}</span> ·{" "}
        <span className="num">{fieldCount}</span> ຊ່ອງ — ໃນແອັບເປັນໜ້າໜຶ່ງ ມີຊື່ຂັ້ນຢູ່ຫົວຈໍ
      </p>
      <div>
        <label className="form-field-label" htmlFor="fp-title">
          ຊື່ຂັ້ນຕອນ (ທີ່ຜູ້ຮ້ອງເຫັນ) <span className="required">*</span>
        </label>
        <input
          id="fp-title"
          className="input-field"
          maxLength={60}
          value={page.title}
          placeholder="ເຊັ່ນ ຂໍ້ມູນຜົວ ຫຼື ເມຍ"
          onChange={(e) => onChange({ title: e.target.value })}
        />
      </div>
      <div>
        <label className="form-field-label" htmlFor="fp-lead">
          ຄຳແນະນຳສັ້ນໆ (ບໍ່ບັງຄັບ)
        </label>
        <textarea
          id="fp-lead"
          className="input-field"
          rows={2}
          maxLength={200}
          value={page.lead}
          placeholder="ເຊັ່ນ ກອກຕາມບັດປະຈຳຕົວຂອງຜົວ ຫຼື ເມຍ"
          onChange={(e) => onChange({ lead: e.target.value })}
        />
      </div>
      <div>
        <label className="form-field-label" htmlFor="fp-scan">
          ປຸ່ມ AI ສະແກນເອກະສານເຕີມໃຫ້
        </label>
        <select
          id="fp-scan"
          className="input-field"
          value={page.scan}
          onChange={(e) => onChange({ scan: e.target.value as FormPage["scan"] })}
        >
          {SCAN_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="caption">
          ຜູ້ຮ້ອງຖ່າຍຮູບເອກະສານນີ້ ແລ້ວ AI ຈະອ່ານ ແລະ ເຕີມສະເພາະຊ່ອງຂອງຂັ້ນນີ້ (ເຕີມແຕ່ຊ່ອງທີ່ຍັງວ່າງ, ຜູ້ຮ້ອງກວດ/ແກ້ໄດ້)
          — ຮູບຖືກແນບໄປກັບຄຳຮ້ອງໃຫ້ເຈົ້າໜ້າທີ່ເບິ່ງ
        </p>
      </div>
      <div className="row" style={{ gap: "var(--space-2)" }}>
        <button type="button" className="btn-outline btn-sm" disabled={index <= 0} onClick={() => onMove(-1)}>
          ↑ ຂຶ້ນ
        </button>
        <button type="button" className="btn-outline btn-sm" disabled={index >= total - 1} onClick={() => onMove(1)}>
          ↓ ລົງ
        </button>
        <button type="button" className="link-action" onClick={onRemove}>
          ລຶບຂັ້ນຕອນນີ້
        </button>
      </div>
      <p className="caption">ລຶບຂັ້ນຕອນ = ຊ່ອງໃນຂັ້ນນີ້ຍ້າຍໄປຢູ່ຂັ້ນກ່ອນໜ້າ (ບໍ່ລຶບຊ່ອງ)</p>
    </div>
  );
}

/** ข้อเสนอของ AI: ขั้นตอน (ใช้ทั้งชุด) + คำแนะนำรายช่อง (เลือกเองทีละข้อ) — ไม่บันทึกจนกด ບັນທຶກຟອມກອກ */
export function AiPagesProposal({
  pages,
  hints,
  labelOf,
  widgetLabel,
  picked,
  onToggle,
  onApply,
  onClose,
}: {
  pages: ProposedFormPage[];
  hints: FormFieldHint[];
  labelOf: (key: string) => string;
  widgetLabel: (widget: string) => string;
  picked: ReadonlySet<string>;
  onToggle: (key: string, on: boolean) => void;
  onApply: () => void;
  onClose: () => void;
}) {
  return (
    <div className="ai-proposals fp-proposal">
      <p className="ai-proposals__head">
        <Sparkles size={14} />
        AI ແນະນຳ <span className="num">{pages.length}</span> ຂັ້ນຕອນ — ກວດແລ້ວກົດໃຊ້ (ແກ້ຕໍ່ໄດ້)
      </p>
      <ol className="fp-proposal__pages">
        {pages.map((p, i) => (
          <li key={`${String(i)}-${p.title_lo}`}>
            <span className="body-sm">
              <b>{p.title_lo}</b> · <span className="num">{p.keys.length}</span> ຊ່ອງ
              {p.scan ? (
                <span className="status-badge status-badge--info" style={{ marginLeft: "var(--space-1-5)" }}>
                  AI ສະແກນ: {scanLabel(p.scan)}
                </span>
              ) : null}
            </span>
            <span className="caption">{p.keys.map(labelOf).join(", ")}</span>
          </li>
        ))}
      </ol>
      {hints.length > 0 ? (
        <>
          <p className="ai-proposals__head">ຄຳແນະນຳເພີ່ມ — ເລືອກອັນທີ່ຈະໃຊ້</p>
          {hints.map((h) => (
            <label key={h.key} className="ai-proposals__item">
              <input type="checkbox" checked={picked.has(h.key)} onChange={(e) => onToggle(h.key, e.target.checked)} />
              <span className="ai-proposals__text">
                <span className="body-sm">
                  {labelOf(h.key)}:{" "}
                  {[
                    h.widget ? `ປ່ຽນເປັນ ${widgetLabel(h.widget)}` : "",
                    h.required === false ? "ບໍ່ບັງຄັບ" : h.required === true ? "ບັງຄັບກອກ" : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                {h.reason_lo ? <span className="caption">“{h.reason_lo}”</span> : null}
              </span>
            </label>
          ))}
        </>
      ) : null}
      <div className="ai-proposals__actions">
        <button type="button" className="btn-secondary btn-sm" onClick={onApply}>
          ໃຊ້ຂັ້ນຕອນນີ້{picked.size > 0 ? ` + ${String(picked.size)} ຄຳແນະນຳ` : ""}
        </button>
        <button type="button" className="btn-outline btn-sm" onClick={onClose}>
          ປິດ
        </button>
      </div>
    </div>
  );
}
